// Client-side mirror of the API's ticket-URL provider inference
// (`apps/api/shared/utils/event_urls.py` — `infer_url_provider` / `registrable_domain` /
// `clean_provider`). Used by the admin URL editors to show the provider a pasted URL will
// resolve to, instead of leaving the field blank until after a save round-trip.
//
// Keep `KNOWN_PROVIDER_ALIASES` and `TWO_PART_TLDS` in sync with the Python module — a
// mismatch means the admin previews a provider the API would not have chosen.

const TWO_PART_TLDS = new Set([
  'co.uk',
  'co.es',
  'com.es',
  'gob.es',
  'org.es',
  'net.es',
  'com.ar',
  'co.ar',
  'com.mx',
  'co.mx',
  'gob.mx',
  'com.br',
  'org.br',
  'com.au',
  'co.nz',
]);

// Map, not a plain object: keys come from user-pasted URLs, so `constructor`/`__proto__`
// must not resolve to anything inherited.
const KNOWN_PROVIDER_ALIASES = new Map([
  ['ticketmaster', 'ticketmaster'],
  ['ticketmaster.com', 'ticketmaster'],
  ['ticketmaster.es', 'ticketmaster'],
  ['ticketmaster.fr', 'ticketmaster'],
  ['ticketmaster.de', 'ticketmaster'],
  ['ticketmaster.nl', 'ticketmaster'],
  ['ticketmaster.it', 'ticketmaster'],
  ['ticketmaster.ie', 'ticketmaster'],
  ['eventbrite', 'eventbrite'],
  ['eventbrite.com', 'eventbrite'],
  ['dice', 'dice'],
  ['dice.fm', 'dice'],
  ['ra.co', 'resident_advisor'],
  ['residentadvisor.net', 'resident_advisor'],
  ['seetickets', 'seetickets'],
  ['seetickets.com', 'seetickets'],
  ['see.tickets', 'seetickets'],
  ['axs', 'axs'],
  ['axs.com', 'axs'],
  ['wegow', 'wegow'],
  ['wegow.com', 'wegow'],
  ['enterticket', 'enterticket'],
  ['enterticket.es', 'enterticket'],
  ['entradas', 'entradas'],
  ['entradas.com', 'entradas'],
  ['viagogo', 'viagogo'],
  ['viagogo.com', 'viagogo'],
  ['stubhub', 'stubhub'],
  ['stubhub.com', 'stubhub'],
  ['livenation', 'livenation'],
  ['livenation.com', 'livenation'],
  ['ticketm.net', 'ticketmaster'],
  ['scdn.co', 'spotify'],
  ['spotifycdn.com', 'spotify'],
  ['fbcdn.net', 'facebook'],
  ['cdninstagram.com', 'instagram'],
  ['fever', 'fever'],
  ['feverup.com', 'fever'],
  ['taquilla', 'taquilla'],
  ['taquilla.com', 'taquilla'],
  ['atrapalo', 'atrapalo'],
  ['atrapalo.com', 'atrapalo'],
]);

// Affiliate-redirect wrappers that the API unwraps (`sanitize_url`) *before* inferring a
// provider, so the stored provider is the real destination (e.g. "ticketmaster"), not the
// wrapper domain. We deliberately do not re-implement the unwrapping here: on save the API
// keeps an explicitly-sent provider over its own inference, so a client-side guess of
// "prf.hn" would overwrite the better answer. Returning "" instead lets the API decide.
const AFFILIATE_WRAPPER_HOST_SUFFIXES = [
  'prf.hn',
  'evyy.net',
  '7eer.net',
  'ojrq.net',
  'sjv.io',
  'pxf.io',
  'hnyj8s.net',
];
const IMPACT_RADIUS_CLICK_PATH = /^\/c\/\d+\/\d+\/\d+/;

const parseUrl = (value) => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) return null;
  try {
    return new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }
};

const cleanHost = (value) => {
  if (typeof value !== 'string') return null;
  const cleaned = value.trim().toLowerCase().replace(/^\.+|\.+$/g, '');
  return cleaned || null;
};

const hostMatchesSuffix = (host, suffix) => !!host && (host === suffix || host.endsWith(`.${suffix}`));

/** Registrable/root domain of a hostname, honouring the two-part TLDs the API knows about. */
export const registrableDomain = (host) => {
  const clean = cleanHost(host);
  if (!clean) return null;

  const stripped = clean.startsWith('www.') ? clean.slice(4) : clean;
  const parts = stripped.split('.');
  if (parts.length < 2) return stripped;

  const lastTwo = parts.slice(-2).join('.');
  if (TWO_PART_TLDS.has(lastTwo) && parts.length >= 3) return parts.slice(-3).join('.');

  return lastTwo;
};

/** True when the URL is a known affiliate redirect whose real provider is hidden behind a wrapper. */
export const isAffiliateWrapperUrl = (url) => {
  const parsed = parseUrl(url);
  if (!parsed) return false;
  const host = cleanHost(parsed.hostname);
  return (
    AFFILIATE_WRAPPER_HOST_SUFFIXES.some((suffix) => hostMatchesSuffix(host, suffix)) ||
    IMPACT_RADIUS_CLICK_PATH.test(parsed.pathname || '')
  );
};

/**
 * Resolve a hostname to a provider id. Mirrors the API's `infer_url_provider` exactly — no
 * URL-shaped heuristics, so it is safe for any URL, not just a ticket link.
 *
 * `inferUrlProvider` layers the ticket-editor guards on top; image credits deliberately skip
 * them, because an affiliate path pattern or a dotless host says nothing about who published
 * an image and the API would have answered with the host either way.
 */
export const providerFromHost = (host) => {
  const clean = cleanHost(host);
  if (!clean) return '';

  const rootDomain = registrableDomain(clean);
  if (!rootDomain) return '';

  const alias =
    KNOWN_PROVIDER_ALIASES.get(rootDomain) || KNOWN_PROVIDER_ALIASES.get(rootDomain.split('.')[0]);
  if (alias) return alias;

  return clean.startsWith('www.') ? clean.slice(4) : clean;
};

/**
 * Infer the provider id the API would store for a ticket URL.
 *
 * `providerFromHost` plus the ticket-editor guards: returns `""` when nothing can be inferred
 * confidently — including affiliate wrappers, where the API's unwrapping does better than we
 * can here. Use `providerFromHost` for anything that is not a ticket link.
 */
export const inferUrlProvider = (url) => {
  if (isAffiliateWrapperUrl(url)) return '';

  const parsed = parseUrl(url);
  const host = parsed && cleanHost(parsed.hostname);
  // A dotless host is never a real ticket domain — it's a half-typed URL ("https://w"), which
  // would otherwise flash a junk provider into the field on every keystroke. Decline instead.
  if (!host || !host.includes('.')) return '';

  return providerFromHost(host);
};
